import * as React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  Box,
  Button,
  Card,
  Hint,
  NoticeBox,
  PopoverMenu,
  RadioButton,
  Table,
  Text,
} from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0AvatarList } from '@factorialco/f0-react'
import { LOCAL_STORAGE_KEYS, LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import isEmpty from 'lodash/isEmpty'

import { observer } from 'mobx-react'

import { getFilterValue } from 'modules/core/components/EmployeeMultiFilters/commonFilters'
import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import { employeeUser } from 'modules/core/store/collections/users'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import useLegalEntitiesFromEmployees from 'modules/employees/hooks/useLegalEntitiesFromEmployees'
import type Employee from 'modules/employees/store/models/Employee'
import { employeeJob, employeeJobAndLevel } from 'modules/job_catalog/store/collections/roles'
import type { Locations } from 'modules/locations/store/collections/locations'
import locations from 'modules/locations/store/collections/locations'
import permissionGroups from 'modules/permissions/store/collections/permissionGroups'
import policyAuthorizationsCollection from 'modules/permissions/store/collections/policyAuthorizations'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import type { Teams } from 'modules/teams/store/collections/teams'
import policies from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import { TimeoffPolicies } from 'modules/timeoff/store/collections/timeoffPolicies/policies'

import { CreatePermissionContext } from './CreatePermissionContext'
import Body from './_Body'
import Header from './_Header'
import SearchBar from './_SearchBar'
import useGroupFilter from './useGroupFilter'

interface Option {
  to?: string
  title: string
  'data-intercom-target'?: string
  onClick?: (event: Event) => void | Promise<void>
}
interface Props {
  employees: Employee[]
  filtersForm: FiltersForm
  loadingPageData: boolean
  canEditInBulk: boolean
  canEditContractsInBulk: boolean
  showAction: boolean
  actionTo: string
  showOptions: boolean
  options: Option[]
  contractVersions: ContractVersions
  universalEmployeesImporterEnabled: boolean
  legacyEmployeeEdition: boolean
  universalImporterContractsImportFeatureFlag: boolean
  universalImporterContractsEditionFeatureFlag: boolean
  hasCompensationsModule: boolean
  showLegalEntitySelectorModal: boolean
  setLegalEntitySelectorModal: (show: boolean) => void
  canSeeLegalEntityEmployees: boolean
  canEditContractsSettings: boolean
}

const useEmployeesIds = createComputed((employees: Employee[]): number[] => {
  return employees.map((employee) => employee.attributes.id)
})

const useSearchFilter = createComputed(
  (search: string, employees: Employee[], contractVersions: ContractVersions) => {
    if (!isEmpty(search)) {
      track(trackingEvents.employee.page.employeeList.searchEmployee, {
        search_by: search,
      })
    }
    // Normalize and split search and target strings for flexible matching
    const normalize = (str: string) =>
      str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .split(/\s+/)
        .filter(Boolean)

    const searchTokens = normalize(search)

    return employees.filter((employee) => {
      if (isEmpty(search)) return true

      const fields = [
        employeeUser(employee).firstName,
        employeeUser(employee).fullName(),
        employeeJobAndLevel(contractVersions, employee),
        employeeJob(contractVersions, employee),
      ]

      // Check if all search tokens are present in any field, regardless of order
      return fields.some((field) => {
        const fieldTokens = normalize(field)
        return searchTokens.every((token) =>
          fieldTokens.some((fieldToken) => fieldToken.includes(token))
        )
      })
    })
  }
)

const useComputedGroupFilter = createComputed(
  (
    groupFilter: string,
    employees: Employee[],
    _locations: Locations,
    _teams: Teams,
    _policies: TimeoffPolicies,
    totalEmployees: Employee[],
    groupOrder: 'asc' | 'desc'
  ) => {
    return useGroupFilter(
      groupFilter,
      employees,
      _locations,
      _teams,
      _policies,
      totalEmployees,
      groupOrder
    )
  }
)

const ListEmployees = ({
  employees,
  filtersForm,
  loadingPageData,
  canEditInBulk,
  canEditContractsInBulk,
  showAction,
  actionTo,
  showOptions,
  options,
  contractVersions,
  universalEmployeesImporterEnabled,
  legacyEmployeeEdition,
  universalImporterContractsImportFeatureFlag,
  universalImporterContractsEditionFeatureFlag,
  hasCompensationsModule,
  showLegalEntitySelectorModal,
  setLegalEntitySelectorModal,
  canSeeLegalEntityEmployees,
  canEditContractsSettings,
}: Props) => {
  const [selectedEmployees, setSelectedEmployees] = React.useState<Employee[]>([])
  const [modalAction, setModalAction] = React.useState<'import' | 'edit'>('import')
  const [showSelectEmployeesTooltip, setShowSelectEmployeesTooltip] = React.useState(false)
  const [showContractsPermissionsModal, setShowContractsPermissionsModal] = React.useState(false)
  const [showLackPermissionsModal, setShowLackPermissionsModal] = React.useState(false)
  const [groupOrder, setGroupOrder] = React.useState<'asc' | 'desc'>('asc')
  const [groupFilter, setGroupFilter] = React.useState<string>('')
  const [isGroupFilter, setIsGroupFilter] = React.useState(false)
  const [selectedGroupFilter, setSelectedGroupFilter] = React.useState<string>('')
  const [importType, setImportType] = React.useState<'employees' | 'contracts' | null>(null)
  const [showEditContractsModeModal, setShowEditContractsModeModal] = React.useState(false)
  const [selectedEditContractsMode, setSelectedEditContractsMode] = React.useState<
    'all' | 'current'
  >('current')
  const [showEditContractsLegalEntityModal, setShowEditContractsLegalEntityModal] =
    React.useState(false)
  const selectedEmployeesIds = useEmployeesIds(selectedEmployees).get()
  const [, setLocalFilterByValue] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.employeesFilter)
  const [localGroupingFilters, setLocalGroupingFilters] = useLocalStorage<[string, 'asc' | 'desc']>(
    LOCAL_STORAGE_KEYS.employeesGroupingMode
  )
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const sortAttribute = filtersForm.get('sortAttribute').value
  const sortDirection = filtersForm.get('sortDirection').value
  const filterKey = LOCAL_STORAGE_KEYS.employeesFilters
  const [searchParams] = useSearchParams()
  const canEditSelectedEmployeesInBulk = searchParams.get('can-edit-selected-employees')

  const [editPermissionsHintIsHidden, setEditPermissionsHintIsHidden] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideEditInBulkPermissionsHint
  )

  const handleOnCloseTooltip = () => {
    setShowSelectEmployeesTooltip(false)
  }

  const handleFinishPermissionsHint = () => {
    navigate(navigation.settings.permissions.fullPath())
  }

  const [filtersLoading, setFiltersLoading] = React.useState(true)

  const {
    filteredModels: filteredEmployees,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Employee>(getFilterValue, filterKey)

  const onFiltersApplied = (defaultFilter?: MultiFilterForm[]) => {
    onApplyFilters(employees, defaultFilter)

    const isAdvancedFilter = !!logicOperator?.fields.logic_operator?.value

    if (isAdvancedFilter) {
      // Advanced filter mode: track filters from the hook
      filters.forEach((filter) => {
        const filterBy = filter.fields.field?.value
        const filterValue = filter.fields.value?.value

        if (filterBy || filterValue) {
          track(trackingEvents.employee.page.employeeList.filterApplied, {
            filter_by: filterBy,
            filter_value: filterBy === 'employee_status' ? filterValue : '',
          })
        }
      })
    }

    // Also track simple filters (from defaultFilter parameter)
    if (defaultFilter && defaultFilter.length > 0) {
      defaultFilter.forEach((filter) => {
        const filterBy = filter.fields.field?.value
        const filterValue = filter.fields.value?.value

        if (filterBy || filterValue) {
          track(trackingEvents.employee.page.employeeList.filterApplied, {
            filter_by: filterBy,
            filter_value: filterBy === 'employee_status' ? filterValue : '',
          })
        }
      })
    }
  }
  const employeesToShow = useSearchFilter(
    debouncedSearch,
    filteredEmployees,
    contractVersions
  ).get()

  const employeeCount = employees.length
  useAsyncData(async () => {
    await Promise.all([
      policyAuthorizationsCollection.authorizePolicies('employee.create', []),
      policyAuthorizationsCollection.authorizePolicies('payroll.manage_payroll', []),
      policyAuthorizationsCollection.authorizePolicies('contracts.edit', employees),
    ])
  }, [employeeCount])

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  useEffect(() => {
    permissionGroups.fetch()
  }, [])

  useUnsafeEffect(() => {
    setLocalFilterByValue(filtersForm.get('filterBy').value)
  }, [filtersForm, setLocalFilterByValue])

  useEffect(() => {
    if (localGroupingFilters) {
      setGroupFilter(localGroupingFilters[0])
      setGroupOrder(localGroupingFilters[1])
      setIsGroupFilter(true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    Promise.all([locations.fetch(), teams.fetch(), memberships.fetch(), policies.fetch()])
  }, [])

  useEffect(() => {
    if (!editPermissionsHintIsHidden && !loadingPageData) {
      setEditPermissionsHintIsHidden(!canEditInBulk)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditInBulk, loadingPageData])

  useEffect(() => {
    if (canEditSelectedEmployeesInBulk === 'false') {
      setShowLackPermissionsModal(true)
    }
  }, [canEditSelectedEmployeesInBulk])

  const computedGroups = useComputedGroupFilter(
    groupFilter,
    employeesToShow,
    locations,
    teams,
    policies,
    employees,
    groupOrder
  ).get()

  const onApplyGroupFilter = () => {
    setGroupFilter(selectedGroupFilter)
    setIsGroupFilter(true)
    setLocalGroupingFilters([selectedGroupFilter, groupOrder])
  }

  const onChangeGroupFilter = (filter: string) => {
    if (filter === '') {
      setIsGroupFilter(false)
      setLocalGroupingFilters(null)
    } else {
      setSelectedGroupFilter(filter)
      track(trackingEvents.employee.page.employeeList.groupEmployees, {
        group_by: filter.replace(/_/g, ' '),
      })
    }
  }

  const onSelectEmployee = (employee: Employee, value: boolean) => {
    if (value) {
      setSelectedEmployees([...selectedEmployees, employee])
    } else {
      setSelectedEmployees(
        selectedEmployees.filter((_employee) => _employee.attributes.id !== employee.attributes.id)
      )
    }
  }

  const onSelectAll = (value: boolean) => {
    if (value) {
      setSelectedEmployees([...employeesToShow])
    } else {
      setSelectedEmployees([])
    }
  }

  // there is just one groupfilter for now
  const countGroupFilters = localGroupingFilters ? 1 : 0
  const localSavedDefaultGrouping = LOCAL_STORAGE_KEYS.employeesDefaultGrouping

  const editableContracts: Record<number, boolean> = useUnsafeMemo(
    () =>
      employees.reduce((acc, employee) => {
        const employeeId = employee.attributes.id
        if (
          employee &&
          policyAuthorizationsCollection.for('contracts.edit', employee) &&
          typeof employeeId === 'number'
        ) {
          acc[employeeId] = true
        }
        return acc
        // eslint-disable-next-line no-type-assertion/no-type-assertion
      }, {} as Record<number, boolean>),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employees, policyAuthorizationsCollection.models.length]
  )

  const canFixSelectedContract = useUnsafeMemo(() => {
    return selectedEmployeesIds.findIndex((id) => editableContracts[id]) >= 0
  }, [selectedEmployeesIds, editableContracts])

  const selectedEmployeesLegalEntities = useLegalEntitiesFromEmployees(selectedEmployees)

  const { company } = useCurrentSession()

  const legalEntities = company.legalEntities

  const createLegalEntityOptions = React.useCallback((entities: typeof legalEntities) => {
    return entities
      .map((le: { legalName: string; id: number }) => ({
        label: le.legalName,
        value: le.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [])

  const [legalEntityOptions, setLegalEntityOptions] = React.useState(() =>
    createLegalEntityOptions(legalEntities)
  )

  const setLegalEntityOptionsByIds = React.useCallback(
    (ids: number[]) => {
      const filteredEntities = legalEntities.filter((le) => ids.includes(le.id))
      setLegalEntityOptions(createLegalEntityOptions(filteredEntities))
    },
    [legalEntities, createLegalEntityOptions]
  )

  const bulkOptions = useUnsafeMemo(() => {
    const bulkMenuOptions: React.ComponentProps<typeof PopoverMenu.TouchableOption>[] = []

    bulkMenuOptions.push({
      title: i18n.t('employees.bulk_edit.profile.menu_label'),
      disabled: !canEditInBulk,
      description: !canEditInBulk
        ? `${i18n.t('employees.bulk_edit.profile.checking_permissions')}...`
        : undefined,
      onClick: () => {
        if (!selectedEmployeesIds.length) {
          setShowSelectEmployeesTooltip(true)
          return
        }

        if (legacyEmployeeEdition) {
          navigate(navigation.employees.edit.fullPath(), {
            state: {
              employeesIds: selectedEmployeesIds,
            },
          })
        } else if (selectedEmployeesLegalEntities.length > 1) {
          setModalAction('edit')
          setLegalEntityOptionsByIds(selectedEmployeesLegalEntities.map((le) => le.attributes.id))
          setLegalEntitySelectorModal(true)
        } else {
          const selectedLegalEntity: string =
            selectedEmployeesLegalEntities[0]?.attributes.id.toString() || ''
          setSelectedLegalEntity(selectedLegalEntity)
          navigate(navigation.employees.edit.fullPath(), {
            state: {
              employeesIds: selectedEmployeesIds,
              selectedLegalEntity,
              importType: 'employees',
            },
          })
        }
      },
    })

    if (Object.keys(editableContracts).length && !hasCompensationsModule) {
      bulkMenuOptions.push({
        title: i18n.t('employees.bulk_edit.fix_conditions.menu_label'),
        onClick: () => {
          if (!selectedEmployeesIds.length) {
            setShowSelectEmployeesTooltip(true)
            return
          }

          if (!canFixSelectedContract) {
            setShowContractsPermissionsModal(true)
            return
          }

          navigate(navigation.employees.bulkFixContracts.fullPath(), {
            state: {
              employeesIds: selectedEmployeesIds,
            },
          })
        },
      })
    }

    if (universalImporterContractsEditionFeatureFlag && Object.keys(editableContracts).length) {
      bulkMenuOptions.push({
        title: i18n.t('employees.bulk_edit.contracts.menu_label'),
        disabled: !canEditContractsInBulk,
        description: !canEditContractsInBulk
          ? `${i18n.t('employees.bulk_edit.contracts.checking_permissions')}...`
          : undefined,
        onClick: () => {
          if (!selectedEmployeesIds.length) {
            setShowSelectEmployeesTooltip(true)
            return
          }

          if (!canFixSelectedContract) {
            setShowContractsPermissionsModal(true)
            return
          }

          if (selectedEmployeesLegalEntities.length > 1) {
            setModalAction('edit')
            setImportType('contracts')
            setLegalEntityOptionsByIds(selectedEmployeesLegalEntities.map((le) => le.attributes.id))
            setLegalEntitySelectorModal(true)
          } else {
            setSelectedLegalEntity(
              selectedEmployeesLegalEntities[0]?.attributes.id.toString() || ''
            )
            setShowEditContractsModeModal(true)
          }
        },
      })
    }

    if (hasCompensationsModule && Object.keys(editableContracts).length) {
      bulkMenuOptions.push({
        title: i18n.t('employees.bulk_edit.additional_compensations'),
        onClick: () => {
          if (!selectedEmployeesIds.length) {
            setShowSelectEmployeesTooltip(true)
            return
          }

          navigate(navigation.employees.additionalCompensations.create.fullPath(), {
            state: { employeeIds: selectedEmployeesIds },
          })
        },
      })
    }

    return bulkMenuOptions
  }, [
    canEditInBulk,
    canEditContractsInBulk,
    editableContracts,
    selectedEmployeesIds,
    navigate,
    navigation,
    canFixSelectedContract,
    universalImporterContractsEditionFeatureFlag,
    hasCompensationsModule,
    legacyEmployeeEdition,
    selectedEmployeesLegalEntities,
    setLegalEntityOptionsByIds,
    setLegalEntitySelectorModal,
  ])

  const supportBulkActions = bulkOptions.length > 0

  const [selectedLegalEntity, setSelectedLegalEntity] = React.useState(
    legalEntities[0]?.id.toString() || ''
  )

  const handleOnCloseModal = () => {
    setLegalEntitySelectorModal(false)
    setImportType(null)
    setModalAction('import')
  }
  const handleOnClickNextModal = () => {
    setLegalEntitySelectorModal(false)
    if (modalAction === 'import') {
      if (importType === 'contracts') {
        navigateToImportContracts(selectedLegalEntity)
        setImportType(null)
      } else {
        navigate(
          navigation.employees.importEmployees
            .fullPath({
              legal_entity_id: selectedLegalEntity,
            })
            .replace(/\*+$/, ''),
          {
            state: { selectedLegalEntity },
          }
        )
      }
    } else if (modalAction === 'edit' && importType === 'contracts') {
      setShowEditContractsModeModal(true)
    } else {
      const selectedLegalEntityId = selectedLegalEntity
      const selectedEmployeesByLegalEntity = selectedEmployees.filter(
        (employee) =>
          parseInt(employeeLegalEntity(employee).attributes.id) === parseInt(selectedLegalEntityId)
      )
      const filteredEmployeeIds = selectedEmployeesByLegalEntity.map(
        (employee) => employee.attributes.id
      )
      navigate(navigation.employees.edit.fullPath(), {
        state: {
          employeesIds: filteredEmployeeIds,
          selectedLegalEntity,
          importType: 'employees',
        },
      })
    }
  }
  const handleOnClickImportEmployees = () => {
    setImportType('employees')
    if (legalEntityOptions.length > 1) {
      setModalAction('import')
      setLegalEntitySelectorModal(true)
    } else {
      const defaultLegalEntity = String(legalEntityOptions[0]?.value ?? '')
      setSelectedLegalEntity(defaultLegalEntity)
      navigate(
        navigation.employees.importEmployees
          .fullPath({
            legal_entity_id: defaultLegalEntity,
          })
          .replace(/\*+$/, ''),
        {
          state: { selectedLegalEntity: defaultLegalEntity },
        }
      )
    }
  }

  const handleOnClickImportContracts = () => {
    setImportType('contracts')
    if (legalEntityOptions.length > 1) {
      setModalAction('import')
      setLegalEntitySelectorModal(true)
    } else {
      const defaultLegalEntity = String(legalEntityOptions[0]?.value ?? '')
      setSelectedLegalEntity(defaultLegalEntity)
      navigateToImportContracts(defaultLegalEntity)
    }
  }

  const navigateToImportContracts = (legalEntityId: string) => {
    navigate(
      navigation.employees.importContracts
        .fullPath({
          legal_entity_id: legalEntityId,
        })
        .replace(/\*+$/, ''),
      {
        state: { selectedLegalEntity: legalEntityId },
      }
    )
  }

  const handleOnCloseEditContractsModeModal = () => {
    setShowEditContractsModeModal(false)
  }

  const handleOnSelectEditContractsMode = (mode: 'all' | 'current') => {
    setSelectedEditContractsMode(mode)
    setShowEditContractsModeModal(false)

    const legalEntityId = selectedLegalEntity
    navigateToEditContracts(mode, legalEntityId)
  }

  const handleOnCloseEditContractsLegalEntityModal = () => {
    setShowEditContractsLegalEntityModal(false)
  }

  const handleOnClickNextEditContractsModal = () => {
    setShowEditContractsLegalEntityModal(false)
    const legalEntityId = selectedLegalEntity
    navigateToEditContracts(selectedEditContractsMode, legalEntityId)
  }

  const navigateToEditContracts = (mode: 'all' | 'current', legalEntityId: string) => {
    const employeesInLegalEntity = selectedEmployees
      .filter(
        (employee) =>
          parseInt(employeeLegalEntity(employee).attributes.id) === parseInt(legalEntityId)
      )
      .map((employee) => employee.attributes.id)
    navigate(navigation.employees.editContracts.fullPath(), {
      state: {
        employeesIds: employeesInLegalEntity,
        selectedLegalEntity: legalEntityId,
        contractMode: mode,
        importType: 'contracts',
      },
    })
  }

  const tableOptions = (
    <Box
      width="full"
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      gap="s8"
      paddingBottom="s12"
    >
      <SearchBar
        search={search}
        onSearch={onSearch}
        filtersForm={filtersForm}
        dense
        onApplyGroupFilter={onApplyGroupFilter}
        onChangeGroupFilter={onChangeGroupFilter}
        onChangeGroupOrder={setGroupOrder}
        countGroupFilters={countGroupFilters}
        onAddFilter={onAddFilter}
        onRemoveFilter={onRemoveFilter}
        onApplyFilters={(defaultFilter) => onFiltersApplied(defaultFilter)}
        onCancel={onCancel}
        onFiltersLoaded={() => setFiltersLoading(false)}
        filters={filters}
        logicOperator={logicOperator}
        localSavedDefaultGrouping={localSavedDefaultGrouping}
        filterKey={filterKey}
        filterDependencies={[sortAttribute, sortDirection]}
        canSeeLegalEntityEmployees={canSeeLegalEntityEmployees}
        canEditContractsSettings={canEditContractsSettings}
      />
      {supportBulkActions && (
        <Hint.Popover.Provider
          steps={1}
          onClose={() => setEditPermissionsHintIsHidden(true)}
          onFinish={handleFinishPermissionsHint}
        >
          <Box flexNoShrink>
            <Hint.Popover
              step={1}
              title={i18n.t('employees.bulk_edit.hint.title')}
              description={i18n.t('employees.bulk_edit.hint.description')}
              actionLabel={i18n.t('employees.bulk_edit.hint.cta_label')}
              position="left bottom"
              isHidden={!!editPermissionsHintIsHidden}
            />
            <PopoverMenu type="list">
              <PopoverMenu.Trigger asChild>
                <Button type="secondary" iconName="list">
                  {i18n.t('employees.bulk_edit.action')}
                </Button>
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="end">
                {bulkOptions.map(PopoverMenu.TouchableOption)}
              </PopoverMenu.Content>
            </PopoverMenu>
          </Box>
        </Hint.Popover.Provider>
      )}
      {showAction && !universalEmployeesImporterEnabled && (
        <Button.Router
          type="primary"
          iconName="add"
          to={actionTo}
          extra={{ 'data-intercom-target': 'employees-new-employee-button' }}
        >
          {i18n.t('employees.new_employee')}
        </Button.Router>
      )}
      {showAction && universalEmployeesImporterEnabled && (
        <Button.Split
          iconName="add"
          label={i18n.t('employees.new_employee')}
          type="primary"
          dropdownAriaLabel={i18n.t('common.more_options')}
          onClick={() => navigate(actionTo)}
          extra={{ 'data-intercom-target': 'employees-split-actions-button' }}
        >
          <Button.Split.Option
            onClick={() => handleOnClickImportEmployees()}
            iconName="upload"
            extra={{ 'data-intercom-target': 'employees-new-import-people-split-option' }}
          >
            {i18n.t('employees.import_employees')}
          </Button.Split.Option>
          {universalImporterContractsImportFeatureFlag && (
            <Button.Split.Option onClick={() => handleOnClickImportContracts()} iconName="upload">
              {i18n.t('import_contract.confirm.submit')}
            </Button.Split.Option>
          )}
        </Button.Split>
      )}
      {showOptions && (
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button
              type="secondary"
              aria-label="Show more"
              iconName="ellipsis"
              extra={{ 'data-intercom-target': 'employees-more-actions-button' }}
            ></Button>
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            {options.map((option, index) => {
              if (option.onClick) {
                return (
                  <PopoverMenu.TouchableOption
                    key={index}
                    title={option.title}
                    onClick={(_event: Event) => option.onClick?.(_event)}
                    extra={{ 'data-intercom-target': option['data-intercom-target'] }}
                  />
                )
              }
              // Legacy import contracts route handling - only active when universalImporterContractsEditionFeatureFlag is disabled
              // TODO: Remove this legacy handling when universalImporterContractsEditionFeatureFlag is fully rolled out
              return option.to === '/employees/list/import_contracts_mode' ? (
                <Box className="sidebar_employees_edit_contracts_button" key={index}>
                  <PopoverMenu.TouchableOption
                    title={option.title}
                    onClick={(_event: Event) => {
                      if (option.to) {
                        navigate(option.to)
                      }
                    }}
                  />
                </Box>
              ) : (
                <PopoverMenu.TouchableOption
                  key={index}
                  title={option.title}
                  onClick={(_event: Event) => {
                    if (option.to) {
                      navigate(option.to)
                    } else if (option.onClick) {
                      option.onClick(_event)
                    }
                  }}
                  extra={{ 'data-intercom-target': option['data-intercom-target'] }}
                />
              )
            })}
          </PopoverMenu.Content>
        </PopoverMenu>
      )}
      {showContractsPermissionsModal && (
        <Modal
          title={i18n.t('employees.bulk_edit.no_contracts_permissions_modal.title')}
          description={i18n.t('employees.bulk_edit.no_contracts_permissions_modal.description')}
          onClose={() => setShowContractsPermissionsModal(false)}
          illustration="alert"
        />
      )}
      {showLackPermissionsModal && (
        <Modal
          title={i18n.t('employees.bulk_edit.limited_access_modal.title')}
          description={i18n.t('employees.bulk_edit.limited_access_modal.description')}
          onClose={() => setShowLackPermissionsModal(false)}
          illustration="alert"
        />
      )}
      {/* Old import contracts mode modal removed - import flow is now simplified */}
      {showLegalEntitySelectorModal && (
        <Modal
          title={
            modalAction === 'edit' && importType === 'contracts'
              ? i18n.t('importer_employees.legal_entity_modal.title.edit_contracts')
              : modalAction === 'import' && importType === 'contracts'
              ? i18n.t('importer_employees.legal_entity_modal.title.import_contracts')
              : modalAction === 'edit'
              ? i18n.t('importer_employees.legal_entity_modal.title.edit')
              : i18n.t('importer_employees.legal_entity_modal.title.import')
          }
          illustration={
            modalAction === 'edit'
              ? 'edit-file'
              : importType === 'contracts'
              ? 'upload'
              : 'people-add'
          }
          description={
            modalAction === 'edit' && importType === 'contracts'
              ? i18n.t('importer_employees.legal_entity_modal.description.edit_contracts')
              : modalAction === 'import' && importType === 'contracts'
              ? i18n.t('importer_employees.legal_entity_modal.description.import_contracts')
              : modalAction === 'edit'
              ? i18n.t('importer_employees.legal_entity_modal.description.edit')
              : i18n.t('importer_employees.legal_entity_modal.description.import')
          }
          onClose={handleOnCloseModal}
          footer={
            <>
              <F0Button
                variant="outline"
                onClick={handleOnCloseModal}
                label={i18n.t('common.cancel')}
              />
              <F0Button
                onClick={handleOnClickNextModal}
                label={i18n.t('common.next')}
                disabled={!selectedLegalEntity}
              />
            </>
          }
        >
          <Box paddingBottom="s0">
            <Text.Normal size="200" weight="bold">
              {i18n.t('importer_employees.legal_entity_modal.sub_title')}
            </Text.Normal>
          </Box>
          <Box
            flexDirection="column"
            gap="s12"
            paddingTop="s16"
            maxHeight="s360"
            overflowY="auto"
            width="full"
          >
            {legalEntityOptions.map((option) => {
              const employeesInLegalEntity = selectedEmployees.filter(
                (employee) =>
                  parseInt(employeeLegalEntity(employee).attributes.id) ===
                  parseInt(String(option.value))
              )
              return (
                <Card
                  key={option.value}
                  onClick={() => setSelectedLegalEntity(String(option.value))}
                >
                  <Card.Title
                    size="small"
                    action={
                      <RadioButton
                        checked={selectedLegalEntity === String(option.value)}
                        onChange={() => setSelectedLegalEntity(String(option.value))}
                        size="medium"
                        value={String(option.value)}
                      />
                    }
                  >
                    {option.label || `Legal Entity ${option.value}`}
                  </Card.Title>
                  {/* Show employee count for both import and edit flows, but with avatars only for edit */}
                  {employeesInLegalEntity.length > 0 && (
                    <Card.Content>
                      {modalAction === 'edit' ? (
                        <Box
                          width="full"
                          flexDirection="row"
                          alignItems="center"
                          justifyContent="flexStart"
                          gap="s12"
                        >
                          <F0AvatarList
                            avatars={employeesInLegalEntity.slice(0, 3).map((employee) => {
                              const user = employeeUser(employee)
                              return {
                                firstName: user.firstName,
                                lastName: user.lastName || '',
                                src: buildUrl(user.avatar),
                                type: 'person',
                              }
                            })}
                            remainingCount={Math.max(0, employeesInLegalEntity.length - 3)}
                            size="sm"
                            type="person"
                          />
                          <Text.Normal size="200" weight="medium">
                            {i18n.t('importer_employees.legal_entity_modal.employee_count', {
                              count: employeesInLegalEntity.length,
                            })}
                          </Text.Normal>
                        </Box>
                      ) : (
                        <Text.Normal size="200" color="grey600">
                          {i18n.t('importer_employees.legal_entity_modal.employee_count', {
                            count: employeesInLegalEntity.length,
                          })}
                        </Text.Normal>
                      )}
                    </Card.Content>
                  )}
                </Card>
              )
            })}
          </Box>
        </Modal>
      )}
      {showEditContractsModeModal && (
        <Modal
          title={i18n.t('import_contract.mode_selector.title_edition')}
          description={i18n.t('import_contract.mode_selector.description')}
          illustration="edit-file"
          onClose={handleOnCloseEditContractsModeModal}
          footer={
            <>
              <F0Button
                variant="outline"
                onClick={handleOnCloseEditContractsModeModal}
                label={i18n.t('common.cancel')}
              />
              <F0Button
                onClick={() => handleOnSelectEditContractsMode(selectedEditContractsMode)}
                label={i18n.t('common.continue')}
              />
            </>
          }
        >
          <Box flexDirection="column" gap="s16">
            <Box flexDirection="column" gap="s12">
              <Card onClick={() => setSelectedEditContractsMode('current')}>
                <Card.Title
                  size="small"
                  action={
                    <RadioButton
                      checked={selectedEditContractsMode === 'current'}
                      onChange={() => setSelectedEditContractsMode('current')}
                      size="medium"
                      value="current"
                    />
                  }
                >
                  {i18n.t('import_contract.mode_selector.import_current.name')}
                </Card.Title>
                <Card.Content>
                  <Text.Normal size="200" color="grey600">
                    {i18n.t('import_contract.mode_selector.import_current.description')}
                  </Text.Normal>
                </Card.Content>
              </Card>

              <Card onClick={() => setSelectedEditContractsMode('all')}>
                <Card.Title
                  size="small"
                  action={
                    <RadioButton
                      checked={selectedEditContractsMode === 'all'}
                      onChange={() => setSelectedEditContractsMode('all')}
                      size="medium"
                      value="all"
                    />
                  }
                >
                  {i18n.t('import_contract.mode_selector.import_all.name')}
                </Card.Title>
                <Card.Content>
                  <Text.Normal size="200" color="grey600">
                    {i18n.t('import_contract.mode_selector.import_all.description')}
                  </Text.Normal>
                </Card.Content>
              </Card>
            </Box>

            <NoticeBox
              paddingY="s24"
              color="viridian100"
              message={i18n.t('import_contract.mode_selector.info')}
            />
          </Box>
        </Modal>
      )}
      {showEditContractsLegalEntityModal && (
        <Modal
          title={i18n.t('importer_employees.legal_entity_modal.title.edit')}
          illustration="edit-file"
          description={i18n.t('importer_employees.legal_entity_modal.description.edit')}
          onClose={handleOnCloseEditContractsLegalEntityModal}
          footer={
            <>
              <F0Button
                variant="outline"
                onClick={handleOnCloseEditContractsLegalEntityModal}
                label={i18n.t('common.cancel')}
              />
              <F0Button
                onClick={handleOnClickNextEditContractsModal}
                label={i18n.t('common.next')}
                disabled={!selectedLegalEntity}
              />
            </>
          }
        >
          <Box paddingBottom="s0">
            <Text.Normal size="200" weight="bold">
              {i18n.t('importer_employees.legal_entity_modal.sub_title')}
            </Text.Normal>
          </Box>
          <Box
            flexDirection="column"
            gap="s12"
            paddingTop="s16"
            maxHeight="s360"
            overflowY="auto"
            width="full"
          >
            {selectedEmployeesLegalEntities.map((legalEntity) => {
              const employeesInLegalEntity = selectedEmployees.filter(
                (employee) =>
                  parseInt(employeeLegalEntity(employee).attributes.id) ===
                  legalEntity.attributes.id
              )
              // Find the corresponding company legal entity to get the proper legalName
              const companyLegalEntity = legalEntities.find(
                (le) => le.id === legalEntity.attributes.id
              )
              return (
                <Card
                  key={legalEntity.attributes.id}
                  onClick={() => setSelectedLegalEntity(String(legalEntity.attributes.id))}
                >
                  <Card.Title
                    size="small"
                    action={
                      <RadioButton
                        checked={selectedLegalEntity === String(legalEntity.attributes.id)}
                        onChange={() => setSelectedLegalEntity(String(legalEntity.attributes.id))}
                        size="medium"
                        value={String(legalEntity.attributes.id)}
                      />
                    }
                  >
                    {companyLegalEntity?.legalName || `Legal Entity ${legalEntity.attributes.id}`}
                  </Card.Title>
                  <Card.Content>
                    <Box
                      width="full"
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="flexStart"
                      gap="s12"
                    >
                      <F0AvatarList
                        avatars={employeesInLegalEntity.slice(0, 3).map((employee) => {
                          const user = employeeUser(employee)
                          return {
                            firstName: user.firstName,
                            lastName: user.lastName || '',
                            src: buildUrl(user.avatar),
                            type: 'person',
                          }
                        })}
                        remainingCount={Math.max(0, employeesInLegalEntity.length - 3)}
                        size="sm"
                        type="person"
                      />
                      <Text.Normal size="200" weight="medium">
                        {i18n.t('importer_employees.legal_entity_modal.employee_count', {
                          count: employeesInLegalEntity.length,
                        })}
                      </Text.Normal>
                    </Box>
                  </Card.Content>
                </Card>
              )
            })}
          </Box>
        </Modal>
      )}
    </Box>
  )

  const tableOptionsBottomCopy = i18n.tp('employees.showing.items', {
    count: employeesToShow.length,
  })

  const tableOptionsBottom = () => (
    <Box width="full" key={employeesToShow.length}>
      <Text.Normal size="100" weight="medium">
        {tableOptionsBottomCopy}
      </Text.Normal>
    </Box>
  )

  if (loadingPageData) {
    return <Loading />
  }

  return (
    <CreatePermissionContext.Provider
      value={{
        authorization: policyAuthorizationsCollection.for('employee.create'),
        managePayroll: policyAuthorizationsCollection.for('payroll.manage_payroll'),
      }}
    >
      <Box width="full">
        {tableOptions}
        {filtersLoading ? (
          <Loading />
        ) : (
          <>
            {!isGroupFilter && (
              <Table fullWidth optionsBottom={tableOptionsBottom} bottomSticky>
                <Header
                  canBulkEdit={supportBulkActions}
                  filtersForm={filtersForm}
                  onSelectAll={onSelectAll}
                  showSelectEmployeesTooltip={showSelectEmployeesTooltip}
                  onClose={handleOnCloseTooltip}
                />
                <Body
                  canBulkEdit={supportBulkActions}
                  employees={employeesToShow}
                  selectedEmployeesIds={selectedEmployeesIds}
                  onSelectEmployee={onSelectEmployee}
                  contractVersions={contractVersions}
                />
              </Table>
            )}
            {isGroupFilter && (
              <Table fullWidth noBottomRadius>
                <Table noRadius hideBorder>
                  <Header
                    filtersForm={filtersForm}
                    onSelectAll={onSelectAll}
                    showSelectEmployeesTooltip={showSelectEmployeesTooltip}
                    onClose={handleOnCloseTooltip}
                  />
                </Table>
                <Table noRadius hideBorder>
                  <Body
                    canBulkEdit={supportBulkActions}
                    employees={employeesToShow}
                    groups={computedGroups}
                    isGroupFilter
                    selectedEmployeesIds={selectedEmployeesIds}
                    onSelectEmployee={onSelectEmployee}
                    contractVersions={contractVersions}
                  />
                </Table>
              </Table>
            )}
          </>
        )}
      </Box>
    </CreatePermissionContext.Provider>
  )
}

export default observer(ListEmployees)
